import { useEffect, useState } from "react";
import {
  AGENT_REQUEST_SECTIONS,
  AGENT_REQUEST_TYPE_LABELS,
  SYSTEM_ONLY_AGENT_REQUEST_TYPES,
  type AgentRequestType,
  isFieldRequired,
  isFieldVisible,
} from "@aam/shared";
import { getSessionToken } from "../hooks/useSession";
import { InlineFormField } from "./InlineFormField";
import { Button } from "./ui/Button";

const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:4000";

interface AgentRequestRow {
  id: string;
  type: AgentRequestType;
  status: string;
  createdAt: string;
  notes: string | null;
}

interface AgentRequestsPanelProps {
  agentId: string;
  canSubmit: boolean;
  excludeTypes?: AgentRequestType[];
}

export function AgentRequestsPanel({ agentId, canSubmit, excludeTypes = [] }: AgentRequestsPanelProps) {
  const excluded = new Set([...excludeTypes, ...SYSTEM_ONLY_AGENT_REQUEST_TYPES]);
  const availableTypes = (Object.keys(AGENT_REQUEST_TYPE_LABELS) as AgentRequestType[]).filter(
    (type) => !excluded.has(type)
  );
  const token = getSessionToken();
  const [requests, setRequests] = useState<AgentRequestRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeType, setActiveType] = useState<AgentRequestType | null>(null);
  const [requestData, setRequestData] = useState<Record<string, unknown>>({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentId, token]);

  async function loadRequests() {
    if (!token) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/agent-requests?agentId=${agentId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json();
      if (res.ok && json.success) setRequests(json.data);
    } finally {
      setLoading(false);
    }
  }

  function setFieldValue(fieldId: string, value: unknown) {
    setRequestData((current) => ({ ...current, [fieldId]: value }));
  }

  async function submitRequest(type: AgentRequestType) {
    if (!token) return;
    const section = AGENT_REQUEST_SECTIONS[type];
    const errors: string[] = [];
    for (const field of section.fields) {
      if (!isFieldVisible(field, requestData)) continue;
      if (!isFieldRequired(field, requestData)) continue;
      const value = requestData[field.id];
      if (value === undefined || value === null || value === "") errors.push(field.label);
    }
    if (errors.length > 0) {
      setError(`Missing: ${errors.join(", ")}`);
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const payload = Object.fromEntries(
        section.fields
          .filter((field) => isFieldVisible(field, requestData))
          .map((field) => [field.id, requestData[field.id]])
      );
      const res = await fetch(`${API_BASE}/api/agent-requests`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ type, formData: payload, agentUserId: agentId }),
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || "Failed to submit request");
      setRequestData({});
      setActiveType(null);
      await loadRequests();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to submit request");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section className="space-y-4 rounded-[var(--card-radius)] border border-border-default bg-surface-raised p-5 shadow-card">
      <div>
        <h2 className="text-lg font-semibold text-content-primary">Agent requests</h2>
        <p className="mt-1 text-sm text-content-secondary">Hierarchy changes, contract terminations, and commission increases.</p>
      </div>

      {canSubmit && availableTypes.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {availableTypes.map((type) => (
            <Button
              key={type}
              type="button"
              size="sm"
              variant="secondary"
              onClick={() => {
                setRequestData({});
                setActiveType(type);
                setError(null);
              }}
            >
              {AGENT_REQUEST_TYPE_LABELS[type]}
            </Button>
          ))}
        </div>
      )}

      {activeType && (
        <div className="space-y-4 rounded-lg border border-border-default bg-surface-overlay p-4">
          <h3 className="text-sm font-semibold text-content-primary">{AGENT_REQUEST_SECTIONS[activeType].title}</h3>
          {AGENT_REQUEST_SECTIONS[activeType].fields.map((field) => (
            <InlineFormField
              key={field.id}
              field={{
                ...field,
                required: isFieldRequired(field, requestData),
              }}
              value={requestData[field.id]}
              onChange={(nextValue) => setFieldValue(field.id, nextValue)}
            />
          ))}
          {error && <p role="alert" className="text-sm text-status-rejected-text">{error}</p>}
          <div className="flex gap-2">
            <Button
              type="button"
              disabled={submitting}
              onClick={() => submitRequest(activeType)}
            >
              {submitting ? "Submitting…" : "Submit request"}
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => setActiveType(null)}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      {loading ? (
        <p role="status" className="text-sm text-content-muted">Loading requests…</p>
      ) : requests.length === 0 ? (
        <p className="rounded-lg border border-dashed border-border-default p-6 text-center text-sm text-content-muted">No requests yet.</p>
      ) : (
        <div className="space-y-2">
          {requests.map((request) => (
            <div key={request.id} className="rounded-lg border border-border-subtle bg-surface-overlay p-3 text-sm">
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium text-content-primary">{AGENT_REQUEST_TYPE_LABELS[request.type]}</span>
                <span className="text-xs font-semibold uppercase text-content-secondary">{request.status}</span>
              </div>
              <p className="mt-1 text-xs text-content-muted">{new Date(request.createdAt).toLocaleString()}</p>
              {request.notes && <p className="mt-1 text-xs text-content-secondary">{request.notes}</p>}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
